import React, { useCallback, useEffect, useMemo, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useIntl, useSelector } from 'umi';
import CardCommon from '@sg/components/CardCommon';
import { Button, Justify, Select, Table, notification } from 'tea-component';
import RenderOperations from '@sg/components/Chart/Operations';
import Chart from '@sg/components/Chart';
import EventWindows from '@sg/components/EventWindows';
import EventSetting from '@sg/components/EventSetting';
import { pickOperations } from '@sg/commonData/pickOperations';
import TableContent from '@sg/components/TableContent';
import { scrollable } from 'tea-component/lib/table/addons';
import DataProcessing from '@sg/components/DataProcessing';
import PlainText from '@sg/components/PlainText';
import MetricCard from '@sg/pages/BusinessManagement/MetricCard';
import * as echarts from 'echarts';
import { chartGradientColors, chartGradientNOColors, dealTypeValue } from '@sg/utils/useChartOptions';
import { xAxisHighLight } from '@sg/utils/chatUtils';
import { getOverviewData, getGrossRevenueTrend, getGrossRevenueByProject, updateGrossRevenueByProject } from '@sg/services/index';
import { projectColums, editProjectColums, DefaultChat, revenueTrendColumns } from './pageConfig';
import { mathFormat, toTwoDecimalNumber, buildPeriod, buildUpdateTime, getYearPrefix } from '@sg/utils/common';
import { height, rendTitle } from '@sg/utils/commonPageMethod';
import PlusIcon from '@sg/assests/svg/plus-icon.svg';
// @ts-ignore
import * as rbd from "react-beautiful-dnd";
import NoData from '@/pages/intelligence/components/NoData';
import Loading from '@intelligence/components/Loading';
import { EDIT_PAGE_KEY_MAP } from '@sg/hooks/useAutoSaveParams';
import AutoSaveModal from '@sg/components/AutoSaveModal';
import RoutePrompt from '@sg/components/RoutePrompt';
import { useGRBPAutoSave } from './hooks/useGRBPAutoSave';
import { isCategoryPredicate, expandCategoriesAfterDrag } from '@sg/utils/tableExpandUtils';
import { useExpandCollapse } from '@/pages/strategic/hooks/useExpandCollapse';
import DecisionPointsList from './components/DecisionPointsList';

import {
  PUBLISHING_OVERVIEW_TOTAL_REVENUE_CARD,
  PUBLISHING_OVERVIEW_TOTAL_PROFIT_CARD,
  TABLE_VS_FIELD,
  COEFFICIENT,
} from '../config';

import moment from 'moment';
import { getAppendixNotesPermission, getButtonPermission } from '@sg/_config/permission';
import { usePermissionReady } from '@sg/hooks/usePermissionReady';
import { createBtnPoint, SGBuriedBtnPoint, OverviewTab } from '@/pages/strategic/utils/buriedPoint';
import EventToggleSetting from '@/pages/strategic/components/EventToggleSetting';
import useEventSetting from '@/pages/strategic/components/EventToggleSetting/useEventSetting';

// forwardRef allows a child functional component to accept a ref from its parent
const PublishingManagement = forwardRef((props: any, ref) => {
  const { formatMessage } = useIntl();
  const { ready: permissionReady } = usePermissionReady();
  const { selectedMonth, onEditingChange, maxDataTime } = props;
  const { expandable, selectable, indentable, injectable, mergeable, draggable } = Table.addons;
  const [expandedKeys, setExpandedKeys] = useState(['2']);
  const expandedKeysRef = useRef<string[]>(['2']);
  const isTrackingRef = useRef<boolean>(false);

  const selectedMonthMoment = useMemo(() => {
    return selectedMonth ? moment(selectedMonth, 'YYYY-MM') : null;
  }, [selectedMonth]);

  // Sync ref with state when state changes from other sources
  useEffect(() => {
    expandedKeysRef.current = expandedKeys;
  }, [expandedKeys]);
  // Revenue Profit 状态
  const showRevenueTrendList = ['line', 'bar', 'table'];
  const [revenueData, setRevenueData] = useState<any>([]);
  const [allData, setAllData] = useState<any>({});
  const [isDPTEditing, setIsDPTEditing] = useState(false);

  const [projectsLoading, setProjectsLoading] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);
  const [overviewData, setOverviewData] = useState<any>(null);
  const [trendUpdateTime, setTrendUpdateTime] = useState<string>('');
  const [projectsData, setProjectsData] = useState<any[]>([]);
  const [originalProjectsData, setOriginalProjectsData] = useState<any[]>([]); // 保存原始数据用于对比
  const [projectsUpdateTime, setProjectsUpdateTime] = useState<string>('');

  const [maxVsVallue, setMaxVsVallue] = useState(120000000);
  // 计算25F-25B的最大值
  const calculateMaxVsVallue = (data: any[]) => {
    let max = 0;
    const compareDiff = (entity: any) => {
      const diff = Math.abs((entity?.['25F'] || 0) - (entity?.['25B'] || 0));
      if (diff > max) {
        max = diff;
      }
    };

    data.forEach((item: any) => {
      compareDiff(item);
      if (Array.isArray(item?.children)) {
        item.children.forEach((child: any) => compareDiff(child));
      }
    });

    setMaxVsVallue(max);
    return data;
  };

  // 生成子孙关系信息（用于拖拽）
  const relations = useMemo(() => {
    const relationMap: any = {};
    const buildRelations = (records: any[], parentId = "") => {
      for (const record of records) {
        if (parentId) {
          relationMap[String(record.id)] = String(parentId);
        }
        if (record.children && Array.isArray(record.children)) {
          buildRelations(record.children, record.id);
        }
      }
    };
    buildRelations(projectsData);
    return relationMap;
  }, [projectsData]);

  const buttonPermission = useMemo(() => ({
    grossRevenueByProject: getButtonPermission('publishing_overview_grossRevenueByProject'),
    decisionPointsList: getButtonPermission('publishing_overview_decisionPointsList'),
    notes: getButtonPermission('publishing_overview_notes'),
  }), [permissionReady]);

  // 生成一级目录选项（用于批量移动下拉框）
  const [GRBPOEditData, setGRBPOEditData] = useState<any[]>([]);
  const GRBPOptions = useMemo(() => GRBPOEditData
    .filter((item: any) => item.isCategory) // 只选择一级目录
    .map((item: any) => ({
      text: item.Project, // label 是一级目录名
      value: String(item.id), // value 是一级目录 id
    })), [GRBPOEditData]);
  // Dynamic MetricCard configs
  const revenueCardConfig = useMemo(() => PUBLISHING_OVERVIEW_TOTAL_REVENUE_CARD(overviewData), [overviewData]);

  const profitCardConfig = useMemo(() => PUBLISHING_OVERVIEW_TOTAL_PROFIT_CARD(overviewData), [overviewData]);

  const [currentMetricsData, setCurrentMetricsData]: any = useState({
    actual: {
      title: 'Monthly Actual',
      date: '',
      data: null,
      content: 'YoY',
      rate: ''
    },
    // forecast: {
    //   title: 'Monthly Forecast',
    //   date: '',
    //   data: null,
    //   content: 'MoM',
    //   rate: ''
    // },
    kpi: {
      title: 'Monthly KPI',
      date: '',
      data: null,
      content: 'KPI Completion',
      rate: ''
    },
  });

  const [showChatsType, setShowChatsType] = useState<any>(DefaultChat);
  const chartRef = useRef<any>(null);
  const {
    chartEventSetting,
    setChartEventSetting,
    eventSettingVisible,
    setEventSettingVisible,
    isChartReady,
    setIsChartReady,
    eventDataByGame,
    showEventsDisabled,
  } = useEventSetting({
    revenueData,
    chartRef,
    showChatsTypeValue: showChatsType?.value,
    activeCard: 'Revenue',
    eventType: 'publishing',
  });

  // Handle Gross Revenue Trend chart type change with buried point tracking
  const handleShowChatsTypeChange = (e: any, downloadValue?: string) => {
    // Track buried point based on the selected value
    // For download, the second parameter is 'download' (only track when downloadValue is provided)
    // For view/line/bar/table, check e.value (but not when it's a download call)
    if (downloadValue === 'download') {
      // Only track download when the second parameter is explicitly 'download'
      // This prevents double tracking since RenderOperations calls change() twice for download
      const btnPoint = createBtnPoint(
        OverviewTab,
        'grossRevenueTrendDownload',
        'Gross Revenue Trend - Download'
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    } else if (e?.value && e.value !== 'download') {
      // Track view/line/bar/table only when e.value is set and it's not 'download'
      const value = e.value;

      // Track View button (when format changes but value stays the same)
      if (e.format !== showChatsType?.format && value === showChatsType?.value) {
        const btnPoint = createBtnPoint(
          OverviewTab,
          'grossRevenueTrendView',
          'Gross Revenue Trend - View'
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      } else if (value === 'line' || value === 'linearea') {
        // Track Line Chart (only if switching to line, not already on line)
        if (showChatsType?.value !== 'line' && showChatsType?.value !== 'linearea') {
          const btnPoint = createBtnPoint(
            OverviewTab,
            'grossRevenueTrendLineChart',
            'Gross Revenue Trend - Line Chart'
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      } else if (value === 'bar') {
        // Track Bar Chart (only if switching to bar, not already on bar)
        if (showChatsType?.value !== 'bar') {
          const btnPoint = createBtnPoint(
            OverviewTab,
            'grossRevenueTrendBarChart',
            'Gross Revenue Trend - Bar Chart'
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      } else if (value === 'table') {
        // Track Table (only if switching to table, not already on table)
        // RenderOperations already prevents duplicate clicks, so we can track directly
        const btnPoint = createBtnPoint(
          OverviewTab,
          'grossRevenueTrendTable',
          'Gross Revenue Trend - Table'
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      }
    }

    // Call the original setter
    setShowChatsType(e);
  };

  const decisionRef = useRef<HTMLDivElement>(null);
  const GRBPRef = useRef<any>(null);

  // Helper function to find project name by id in projectsData
  const findProjectNameById = useCallback((id: string): string => {
    const findInData = (data: any[]): string | null => {
      for (const record of data) {
        if (String(record.id) === String(id)) {
          // Return Project name
          return record.Project || 'Unknown';
        }
        if (record.children && Array.isArray(record.children)) {
          const found: string | null = findInData(record.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findInData(projectsData) || 'Unknown';
  }, [projectsData]);

  // Internal function to handle expanded keys change with tracking control
  const handleExpandedKeysChangeInternal = useCallback((keys: string[], shouldTrack: boolean = true) => {
    // Prevent double tracking
    if (isTrackingRef.current) {
      setExpandedKeys(keys);
      expandedKeysRef.current = keys;
      return;
    }

    // Use ref to get the previous keys to avoid stale closure issues
    const previousKeys = expandedKeysRef.current;

    // Only track if keys actually changed
    const previousKeysSorted = [...previousKeys].sort().join(',');
    const keysSorted = [...keys].sort().join(',');
    const keysChanged = previousKeysSorted !== keysSorted;

    if (!keysChanged) {
      setExpandedKeys(keys);
      expandedKeysRef.current = keys;
      return;
    }

    // Set tracking flag to prevent double tracking
    isTrackingRef.current = true;

    // Only track buried points if shouldTrack is true (user manually clicked project)
    if (shouldTrack) {
      // Find which project was expanded or collapsed
      const newlyExpanded = keys.filter(key => !previousKeys.includes(key));
      const newlyCollapsed = previousKeys.filter(key => !keys.includes(key));

      // Track expansion
      if (newlyExpanded.length > 0) {
        newlyExpanded.forEach(key => {
          const projectName = findProjectNameById(key);
          const btnPoint = createBtnPoint(
            OverviewTab,
            'grossRevenueByProjectsCollapse',
            `Gross Revenue by Projects Expand - ${projectName}`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        });
      }

      // Track collapse
      if (newlyCollapsed.length > 0) {
        newlyCollapsed.forEach(key => {
          const projectName = findProjectNameById(key);
          const btnPoint = createBtnPoint(
            OverviewTab,
            'grossRevenueByProjectsCollapse',
            `Gross Revenue by Projects Collapse - ${projectName}`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        });
      }
    }

    // Update the state and ref, then reset tracking flag
    setExpandedKeys(keys);
    expandedKeysRef.current = keys;

    // Reset tracking flag after a short delay to allow state update
    setTimeout(() => {
      isTrackingRef.current = false;
    }, 0);
  }, [findProjectNameById]);

  // Wrapper for expandable addon - always tracks (user clicked project)
  const handleExpandedKeysChange = useCallback((keys: string[], context?: any) => {
    handleExpandedKeysChangeInternal(keys, true);
  }, [handleExpandedKeysChangeInternal]);

  // 父组件维护的编辑状态
  const [isGRBPEditing, setIsGRBPEditing] = useState(false);
  const [isNotesEditing, setIsNotesEditing] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0); // Gross Revenue 选中的项目数量
  const [moveToValue, setMoveToValue] = useState<string | undefined>(undefined); // 批量移动的目标分类

  // 展开/折叠所有有 children 的目录 (must be defined before useGRBPAutoSave hook)
  const { isAllExpanded, toggleExpandAll, expandAllCategoriesWithChildren } = useExpandCollapse({
    expandedKeys,
    setExpandedKeys,
    expandedKeysRef,
    data: projectsData,
    predicate: isCategoryPredicate,
  });

  // 🚀 Auto-save for Gross Revenue by Projects - Using custom hook
  const {
    GRBPAutoSaveVisible,
    hasGRBPUnsavedChanges,
    handleGRBPEdit,
    handleGRBPAutoSaveCancel,
    handleGRBPAutoSaveConfirm,
    clearGRBPAutoSave,
  } = useGRBPAutoSave(isGRBPEditing, projectsData, GRBPRef, setProjectsData, expandAllCategoriesWithChildren);


  const { userInfo } = useSelector((state: any) => ({
    userInfo: state?.home?.userInfo,
  }));
  const { userid = '' } = userInfo || {};

  // 通用函数：重新计算每个一级目录的汇总数据（25A、25F、25B）
  const recalculateCategoryTotals = useCallback((data: any[]) => {
    return data.map((category: any) => {
      // 如果有 children 属性（一级目录），重新计算汇总
      if (category.children !== undefined) {
        if (Array.isArray(category.children) && category.children.length > 0) {
          // 有子项目：计算所有二级项目的汇总
          const totals = category.children.reduce(
            (acc: any, child: any) => ({
              '25A': (acc['25A'] || 0) + (child['25A'] || 0),
              '25F': (acc['25F'] || 0) + (child['25F'] || 0),
              '25B': (acc['25B'] || 0) + (child['25B'] || 0),
            }),
            { '25A': 0, '25F': 0, '25B': 0 }
          );

          return {
            ...category,
            '25A': totals['25A'],
            '25F': totals['25F'],
            '25B': totals['25B'],
          };
        }
        // 没有子项目：设置为 0
        return {
          ...category,
          '25A': 0,
          '25F': 0,
          '25B': 0,
        };
      }
      // 二级项目保持不变
      return category;
    });
  }, []);

  // Fetch Gross Revenue By Project data
  const fetchGrossRevenueByProject = useCallback(async () => {
    try {
      setProjectsLoading(true);

      // Build payload with optional datatime parameter
      const payload: any = {};
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }

      const res: any = await getGrossRevenueByProject(payload);
      // console.log("Gross Revenue By Project API Response:", res);

      // Transform new API structure to match old table structure
      const transformedData = res?.data?.categories?.flatMap((category: any) => {
        const categoryRow = {
          id: `category_${category.category_name}`, // Prefix with 'category_' to avoid collisions with project IDs
          Project: category.category_name,
          '25A': category.total_row['25A'],
          '25F': category.total_row['25F'],
          '25B': category.total_row['25B'],
          'Opportunities & Risks': category.total_row.risk || '',
          'Action & Note': category.total_row.note || '',
          isCategory: true, // 明确标识为一级目录
          children: category.projects.map((project: any) => ({
            id: project.project_id,
            Project: project.project_name,
            '25A': project['25A'],
            '25F': project['25F'],
            '25B': project['25B'],
            'Opportunities & Risks': project.risk || '',
            'Action & Note': project.note || '',
            isCategory: false, // 明确标识为二级项目
          }))
        };
        return categoryRow;
      }) || [];
      calculateMaxVsVallue(transformedData);
      setProjectsData(transformedData);
      // 自动展开第一个类别
      if (transformedData.length >= 1) {
        setExpandedKeys([String(transformedData[0].id)]);
      }
      setOriginalProjectsData(JSON.parse(JSON.stringify(transformedData))); // 深拷贝保存原始数据
      setProjectsUpdateTime(res?.data?.datatime || '');
    } catch (error) {
      notification.error({
        description: 'Error fetching projects data',
      });
      setProjectsData([]);
    } finally {
      setProjectsLoading(false);
    }
  }, [calculateMaxVsVallue, selectedMonth]);

  // Gross Revenue by Projects 状态变化回调
  const handleGRBPEditingChange = useCallback((isEditing: boolean) => {
    setIsGRBPEditing(isEditing);
    // 进入编辑模式时，展开所有有 children 的目录
    if (isEditing) {
      const currentData = GRBPRef.current?.getTableData?.() || [];
      expandAllCategoriesWithChildren(currentData);
    } else {
      // 退出编辑时清空选择状态
      setSelectedCount(0);
      setMoveToValue(undefined);
      // Clear auto-save when user cancels/exits edit mode
      clearGRBPAutoSave();
    }

    // Notify parent component about editing state change to disable the date selection
    if (onEditingChange) {
      onEditingChange(isEditing || isDPTEditing || isNotesEditing);
    }
  }, [expandAllCategoriesWithChildren, clearGRBPAutoSave, onEditingChange, isDPTEditing, isNotesEditing]);

  // Notes editing change handler
  const handleNotesEditingChange = useCallback((isEditing: boolean) => {
    setIsNotesEditing(isEditing);

    // Notify parent component about editing state change to disable the date selection
    if (onEditingChange) {
      onEditingChange(isGRBPEditing || isDPTEditing || isEditing);
    }
  }, [onEditingChange, isGRBPEditing, isDPTEditing]);

  // 👇 expose methods to parent
  useImperativeHandle(ref, () => ({
    scrollToDecisionPoints: () => {
      decisionRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
  }));

  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 400);
  }, []);

  useEffect(() => {
    setShowRevenueTrendList(revenueData);
  }, [revenueData, showChatsType, formatMessage, chartEventSetting.showEventBubbles]); // Added formatMessage for toggling language in charts

  const fetchGrossRevenueTrend = async () => {
    setTrendLoading(true);
    try {
      const payload: any = {};

      // Add datatime if selectedMonth is available
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }

      const res: any = await getGrossRevenueTrend(payload);
      const apiData = res?.data?.data || [];
      setRevenueData(apiData);
      setTrendUpdateTime(res?.data?.datatime || '');

      // Get the last month's data before actual hits null
      const lastValidData = apiData.findLast((item: any) => item['25A'] !== null);

      if (lastValidData) {
        setCurrentMetricsData({
          actual: {
            title: 'Monthly Actual',
            date: lastValidData.month,
            data: lastValidData['25A'],
            // 接口 /api/v1/mgmt_pc/publishing/monthly-revenue 不再返回 25Ayoy 参数，移除 YoY 显示
            // content: 'YoY',
            // rate: lastValidData['25Ayoy']?.toFixed(1) || ''
            content: '',
            rate: ''
          },
          // forecast: {
          //   title: 'Monthly Forecast',
          //   date: lastValidData.month,
          //   data: lastValidData['25F'],
          //   content: 'MoM',
          //   rate: lastValidData['25Fmom']?.toFixed(1) || ''
          // },
          kpi: {
            title: 'Monthly KPI',
            date: lastValidData.month,
            data: lastValidData['25B'],
            content: 'KPI Completion',
            rate: lastValidData.kpi_completion?.toFixed(1) || ''
          },
        });
      } else {
        // Reset to initial empty state when there's no valid data
        setCurrentMetricsData({
          actual: {
            title: 'Monthly Actual',
            date: '',
            data: null,
            content: '',
            rate: ''
          },
          kpi: {
            title: 'Monthly KPI',
            date: '',
            data: null,
            content: 'KPI Completion',
            rate: ''
          },
        });
      }
    } catch (error) {
      console.error('Failed to fetch gross revenue trend:', error);
      notification.error({
        description: 'Failed to load gross revenue trend data. Please try again.',
      });
      setRevenueData([]);
    } finally {
      setTrendLoading(false);
    }
  };

  const fetchOverviewData = async () => {
    try {
      const payload: any = {};

      // Add datatime if selectedMonth is available
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }

      const res: any = await getOverviewData(payload);
      // console.log("Monthly Revenue API Response:", res);
      setOverviewData(res?.data);
    } catch (error) {
      notification.error({
        description: 'Error fetching overview data',
      });
      setOverviewData(null);
    }
  };


  // 添加新项目行
  const handleAddProject = () => {
    const timestamp = Date.now();
    const newProject = {
      id: `new_project_${timestamp}`, // 添加 id 字段用于识别新增的 category
      Project: '',
      '25A': null,
      '25F': null,
      '25B': null,
      'Opportunities & Risks': '',
      'Action & Note': '',
      isCategory: true, // 明确标识为一级目录
      children: [], // 新增的 category 初始没有子项目
    };

    if (GRBPRef.current) {
      // 更新 editData 和 tableData，在顶部添加新行
      GRBPRef.current.setEditData((prevData: any[]) => [newProject, ...prevData]);
      GRBPRef.current.setTableData((prevData: any[]) => [newProject, ...prevData]);
    }
  };

  // 保存 Gross Revenue by Projects 数据
  const handleSaveProjects = async (editedData: any[]) => {
    try {
      setProjectsLoading(true);

      // console.log('💾 [SAVE] handleSaveProjects called with editedData:', editedData.length, 'categories');
      // console.log('💾 [SAVE] originalProjectsData has:', originalProjectsData.length, 'categories');

      const apiData: any = {
        categories_to_delete: [],
        categories_to_create: [],
        categories_to_update: [],
        category_notes: [],
        category_orders: [],
        projects_to_move: [],
        project_orders: [],
        project_notes: [],
        datatime: projectsUpdateTime,
      };

      // 构建原始数据的 Map（用于快速查找）
      const originalCategoriesMap = new Map();
      const originalProjectsMap = new Map();

      originalProjectsData.forEach((category: any) => {
        originalCategoriesMap.set(category.id, category);
        if (category.children && Array.isArray(category.children)) {
          category.children.forEach((project: any) => {
            originalProjectsMap.set(project.id, {
              ...project,
              category: category.id, // 记录原始所属的一级目录
            });
          });
        }
      });

      // 构建编辑后数据的 Map（用于快速查找）
      const editedCategoriesMap = new Map();
      editedData.forEach((category: any) => {
        editedCategoriesMap.set(category.id, category);
      });

      // 检查被删除的 categories
      originalProjectsData.forEach((originalCategory: any) => {
        if (!editedCategoriesMap.has(originalCategory.id)) {
          apiData.categories_to_delete.push(originalCategory.Project);
        }
      });

      // 检查新增的 categories
      editedData.forEach((editedCategory: any) => {
        if (!originalCategoriesMap.has(editedCategory.id)) {
          apiData.categories_to_create.push({
            category_name: editedCategory.Project
          });
        }
      });

      // 检查名字改变的 categories 和 Sustainability 变动
      editedData.forEach((editedCategory: any, index: number) => {
        const originalCategory = originalCategoriesMap.get(editedCategory.id);
        if (originalCategory) {
          // 检查名字是否改变
          if (originalCategory.Project !== editedCategory.Project) {
            apiData.categories_to_update.push({
              old_category_name: originalCategory.Project,
              new_category_name: editedCategory.Project
            });
          }

          // 检查 Opportunities & Risks 或 Action & Note 是否变化
          const riskChanged = originalCategory['Opportunities & Risks'] !== editedCategory['Opportunities & Risks'];
          const noteChanged = originalCategory['Action & Note'] !== editedCategory['Action & Note'];

          if (riskChanged || noteChanged) {
            apiData.category_notes.push({
              category_name: editedCategory.Project, // 使用编辑后的名字
              risk: editedCategory['Opportunities & Risks'] || '',
              note: editedCategory['Action & Note'] || ''
            });
          }

          // 记录一级目录顺序
          apiData.category_orders.push({
            category_name: editedCategory.Project, // 使用编辑后的名字
            order: index + 1
          });
        } else {
          // 新增的 category，检查是否有 notes 内容
          const hasRiskContent = editedCategory['Opportunities & Risks'] && editedCategory['Opportunities & Risks'].trim() !== '';
          const hasNoteContent = editedCategory['Action & Note'] && editedCategory['Action & Note'].trim() !== '';

          if (hasRiskContent || hasNoteContent) {
            apiData.category_notes.push({
              category_name: editedCategory.Project,
              risk: editedCategory['Opportunities & Risks'] || '',
              note: editedCategory['Action & Note'] || ''
            });
          }
        }

        // 检查二级项目的变动
        if (editedCategory.children && Array.isArray(editedCategory.children)) {
          editedCategory.children.forEach((editedProject: any, pIndex: number) => {
            const originalProject = originalProjectsMap.get(editedProject.id);
            if (originalProject) {
              // 检查项目是否移动到其他一级目录
              const categoryId = editedCategory.id;
              const isMoved = originalProject.category !== categoryId;

              // 如果移动了，记录到 projects_to_move
              if (isMoved) {
                apiData.projects_to_move.push({
                  project_id: editedProject.id,
                  new_category_name: editedCategory.Project // 使用编辑后的一级目录名
                });
                // 同时记录在新目录中的排序位置
                apiData.project_orders.push({
                  project_id: editedProject.id,
                  order: pIndex + 1
                });
              } else {
                // 如果没有移动，记录排序
                apiData.project_orders.push({
                  project_id: editedProject.id,
                  order: pIndex + 1
                });
              }

              // 检查项目数据是否变化
              const riskChanged = originalProject['Opportunities & Risks'] !== editedProject['Opportunities & Risks'];
              const noteChanged = originalProject['Action & Note'] !== editedProject['Action & Note'];

              // 如果数据变化了，记录到 project_notes
              if (riskChanged || noteChanged) {
                apiData.project_notes.push({
                  project_id: editedProject.id,
                  risk: editedProject['Opportunities & Risks'] || '',
                  note: editedProject['Action & Note'] || ''
                });
              }
            }
          });
        }
      });

      // console.log('💾 [SAVE] Prepared API data:', JSON.stringify({
      //   categories_to_delete: apiData.categories_to_delete,
      //   categories_to_create: apiData.categories_to_create,
      //   categories_to_update: apiData.categories_to_update,
      //   category_notes_count: apiData.category_notes.length,
      //   category_orders_count: apiData.category_orders.length,
      //   projects_to_move: apiData.projects_to_move,
      //   project_orders_count: apiData.project_orders.length,
      //   project_notes_count: apiData.project_notes.length,
      // }, null, 2));

      // 调用 API
      await updateGrossRevenueByProject(apiData);

      // Clear auto-save data on successful save
      clearGRBPAutoSave();

      // 刷新数据
      await fetchGrossRevenueByProject();

      setProjectsLoading(false);
    } catch (error) {
      console.error('保存失败:', error);
      notification.error({
        description: 'Failed to save gross revenue by project. Please try again.',
      });
      setProjectsLoading(false);
    }
  };



  const init = () => {
    fetchGrossRevenueTrend();
    fetchOverviewData();
    fetchGrossRevenueByProject();
  };

  // Refetch data when selected month changes
  useEffect(() => {
    init();
  }, [selectedMonth]);

  const setShowRevenueTrendList = (data: any) => {
    const chartData: any = {};
    if (showRevenueTrendList.length === 0 || !data || data.length === 0) return;
    showRevenueTrendList.map((item) => {
      if (item === 'table') {
        // Transform data for table
        const tableData = data.map((element: any) => ({
          date: element.month,
          Actuals: element['25A'],
          Forecasts: element['25F'],
          KPIs: element['25B'],
        }));

        chartData[item] = {
          data: tableData,
          columns: revenueTrendColumns(),
        };
        return '';
      }

      const xAxisData: any = [];
      const datas: any = {};
      const actualLineColor = chartGradientColors?.[0]?.[1] || '#5EC8F1';
      const forecastLineColor = chartGradientColors?.[1]?.[1] || '#7F6FF1';

      // Calculate cumulative values for line chart, use monthly values for bar chart
      if (showChatsType?.value === 'line') {
        // For line chart: calculate cumulative (YTD) values
        let cumulativeActual: number | null = null;
        let cumulativeKpi: number | null = null;
        let lastActualValue: number | null = null;
        let lastActualIndex: number | null = null;

        datas.Actual = [];
        datas.Forecast = [];
        datas.KPI = [];
        datas.yoyData = []; // Store YoY data for tooltips

        data.forEach((element: any, idx: number) => {
          xAxisData.push(element.month);

          if (element['25A'] !== null && element['25A'] !== undefined) {
            if (cumulativeActual === null) {
              cumulativeActual = element['25A'];
            } else {
              cumulativeActual += element['25A'];
            }
            // 接口 /api/v1/mgmt_pc/publishing/monthly-revenue 不再返回 25Ayoy 参数，移除 YoY 提示
            // const yoyValue = element['25Ayoy'];
            // const yoyFormatted = yoyValue !== null && yoyValue !== undefined ? yoyValue.toFixed(1) : '';
            // const yoyArrow = yoyValue && yoyValue > 0 ? '↑' : yoyValue && yoyValue < 0 ? '↓' : '';
            // const yoyColor = yoyValue && yoyValue > 0 ? '#29B569' : yoyValue && yoyValue < 0 ? '#FF4D4F' : '#9B9FB0';
            // const tip = yoyFormatted
            //   ? `<span style=\"margin-left: 2px; line-height: '32px'; font-size: 12px\">(YoY <span style=\"color:${yoyColor}\">${yoyFormatted}% ${yoyArrow}</span>)</span>`
            //   : '';
            const tip = '';
            datas.Actual.push({
              value: cumulativeActual,
              names: formatMessage({ id: 'dbs_charts_legend_actual' }),
              tip,
              render: null,
              totalShowContent: null,
              textColor: undefined,
            });
            datas.Forecast.push({
              value: null,
              names: formatMessage({ id: 'dbs_charts_legend_forecast' }),
              tip: '',
              render: null,
              totalShowContent: null,
              textColor: undefined,
            });
            // datas.yoyData.push(element['25Ayoy']);
            datas.yoyData.push(null);
            lastActualValue = cumulativeActual;
            lastActualIndex = idx;
          } else if (element['25F'] !== null && element['25F'] !== undefined) {
            if (cumulativeActual === null) {
              cumulativeActual = lastActualValue ?? 0;
            }
            cumulativeActual += element['25F'];
            datas.Actual.push({
              value: null,
              tooltipValue: 0,
              names: formatMessage({ id: 'dbs_charts_legend_actual' }),
              tip: '',
              render: null,
              totalShowContent: null,
              textColor: undefined,
            });
            datas.Forecast.push({
              value: cumulativeActual,
              names: formatMessage({ id: 'dbs_charts_legend_forecast' }),
              tip: '',
              render: null,
              totalShowContent: null,
              textColor: undefined,
            });
            datas.yoyData.push(null);
          } else {
            cumulativeActual = null;
            datas.Actual.push({
              value: null,
              names: formatMessage({ id: 'dbs_charts_legend_actual' }),
              tip: '',
              render: null,
              totalShowContent: null,
              textColor: undefined,
            });
            datas.Forecast.push({
              value: null,
              names: formatMessage({ id: 'dbs_charts_legend_forecast' }),
              tip: '',
              render: null,
              totalShowContent: null,
              textColor: undefined,
            });
            datas.yoyData.push(null);
          }

          if (element['25B'] === null) {
            cumulativeKpi = null;
          } else if (cumulativeKpi === null) {
            cumulativeKpi = element['25B'];
          } else {
            cumulativeKpi += element['25B'];
          }

          datas.KPI.push(cumulativeKpi);
        });

        if (lastActualIndex !== null && lastActualIndex >= 0 && lastActualValue !== null) {
          const forecastEntry = datas.Forecast[lastActualIndex];
          const forecastValue = typeof forecastEntry === 'object' ? forecastEntry?.value : forecastEntry;
          if (forecastValue === null || forecastValue === undefined) {
            datas.Forecast[lastActualIndex] = {
              value: lastActualValue,
              names: formatMessage({ id: 'dbs_charts_legend_forecast' }),
            };
          }
        }
      } else if (showChatsType?.value === 'bar') {
        // For bar chart: use monthly values
        datas.Actual = [];
        datas.Forecast = [];
        datas.KPI = [];
        datas.yoyData = []; // Store original YoY value

        data.forEach((element: any) => {
          xAxisData.push(element.month);
          // Handle null values - push null as null to preserve the distinction
          datas.Actual.push(element['25A']);
          datas.Forecast.push(element['25F']);
          datas.KPI.push(element['25B']);
          // 接口 /api/v1/mgmt_pc/publishing/monthly-revenue 不再返回 25Ayoy 参数
          // datas.yoyData.push(element['25Ayoy']); // Store original YoY value
          datas.yoyData.push(null);
        });
      }
      
      xAxisData.push('');

      chartData[item] = {
        xAxisData,
        type: showChatsType?.value,
        seriesData: Object.keys(datas).filter(key => key !== 'yoyData' && key !== 'segmentStart').map((keys, index) => {
          const tdata: any = {
            name: formatMessage({ id: `dbs_charts_legend_${keys.toLowerCase()}` }),
            barGap: showChatsType?.value === 'bar' ? '-100%' : '0%',
            barWidth: '25%',
            data: datas[keys].map((items: any, i: number) => {
              if (items && typeof items === 'object' && items.value !== undefined) {
                return items;
              }
              if (showChatsType?.value === 'line') {
                return {
                  value: items ?? null,
                  render: undefined,
                  totalShowContent: undefined,
                  textColor: undefined,
                };
              }
              if (showChatsType?.value === 'bar' && keys !== 'KPI') {
                const yoyValue = datas.yoyData?.[i];
                const yoyFormatted = yoyValue !== null && yoyValue !== undefined ? yoyValue.toFixed(1) : '';
                const yoyArrow = yoyValue > 0 ? '↑' : yoyValue < 0 ? '↓' : '';
                const yoyColor = yoyValue > 0 ? '#29B569' : yoyValue < 0 ? '#FF4D4F' : '#9B9FB0';
                if (datas?.Actual[i]) {
                  return {
                    value: items,
                    names: keys === 'Actual' ? 'Gross Revenue' : '',
                    tip: keys === 'Actual' && items && yoyFormatted &&
                      `<span style="margin-left: 2px; line-height: '32px'; font-size: 12px">(YoY <span style="color:${yoyColor}">${yoyFormatted}% ${yoyArrow}</span>)</span>`,
                    itemStyle: {
                      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: chartGradientColors[index]?.[1] || '#5EC8F1' },
                        { offset: 1, color: chartGradientColors[index]?.[0] || '#56BCE6' }
                      ]),
                    }
                  };
                }
                return {
                  value: items,
                  names: keys === 'Actual' ? 'Gross Revenue' : '',
                  itemStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                      { offset: 0, color: chartGradientNOColors[index]?.[1] || '#E0E0E0' },
                      { offset: 1, color: chartGradientNOColors[index]?.[0] || '#F5F5F5' }
                    ]),
                  }
                };
              }
              if (showChatsType?.value === 'line') {
                if (keys === 'Actual') {
                  if (items === null || items === undefined) return null;
                  const yoyValue = datas.yoyData?.[i];
                  const yoyFormatted = yoyValue !== null && yoyValue !== undefined ? yoyValue.toFixed(1) : '';
                  const yoyArrow = yoyValue && yoyValue > 0 ? '↑' : yoyValue && yoyValue < 0 ? '↓' : '';
                  const yoyColor = yoyValue && yoyValue > 0 ? '#29B569' : yoyValue && yoyValue < 0 ? '#FF4D4F' : '#9B9FB0';

                  return {
                    value: items,
                    names: formatMessage({ id: 'dbs_charts_legend_actual' }),
                    tip:
                      items && yoyFormatted
                        ? `<span style="margin-left: 2px; line-height: '32px'; font-size: 12px">(YoY <span style="color:${yoyColor}">${yoyFormatted}% ${yoyArrow}</span>)</span>`
                        : '',
                  };
                }
                if (keys === 'Forecast') {
                  if (items === null || items === undefined) return null;
                  return {
                    value: items,
                    names: formatMessage({ id: 'dbs_charts_legend_forecast' }),
                  };
                }
              }
              return items;
            }),
            type: keys === 'KPI' && showChatsType?.value === 'bar' ? 'line' : showChatsType?.value,
            z: showChatsType?.value === 'bar' ? keys === 'KPI' ? 99 : Object.keys(datas).length - index : keys === 'Actual' ? 99 : Object.keys(datas).length - index,
            showSymbol: showChatsType?.format,
          };
          if (showChatsType?.value === 'line' || showChatsType?.value === 'bar') {
            tdata.lineStyle = {
              type: showChatsType?.value === 'bar' || keys === 'Actual' ? '' : 'dashed',
              width: 2,
            };
            tdata.label = {
              show: keys === 'Actual' ? showChatsType?.format : false,
              formatter: (params: any) => mathFormat(params?.value),
              position: showChatsType?.value === 'line' ? ['top', 'bottom'][index % 2] : 'top',
            };
          }
          if (showChatsType?.value === 'line' && keys === 'Forecast') {
            tdata.itemStyle = {
              color: forecastLineColor,
            };
            tdata.lineStyle = {
              color: forecastLineColor,
              width: 2,
              type: 'dashed',
            };
            tdata.connectNulls = true;
          }
          if (showChatsType?.value === 'line' && keys === 'Actual') {
            tdata.itemStyle = {
              color: actualLineColor,
            };
            tdata.lineStyle = {
              color: actualLineColor,
              width: 2,
              type: 'solid',
            };
            tdata.connectNulls = false;
          }
          return tdata;
        })
      };
      // Add TopTipsEvent series for event positioning (hidden series used by EventWindows)
      if (chartEventSetting.showEventBubbles) {
        const xAxisDataLength = xAxisData.length;
        const isBarChart = showChatsType?.value === 'bar';
        chartData[item].seriesData.push({
          name: 'TopTipsEvent',
          type: isBarChart ? 'bar' : 'line',
          ...(isBarChart
            ? {
              barWidth: '25%',
              barGap: '-100%',
            }
            : {}),
          data: new Array(xAxisDataLength).fill(0),
          itemStyle: { color: 'transparent', opacity: 0 },
          lineStyle: { opacity: 0 },
          label: { show: false },
          tooltip: { show: false },
          silent: true,
          z: -1,
          hiddenFromLegend: true,
        });
      }
    });
    setAllData(chartData);
  };

  const currentRevenueTrendChartData = useMemo(() => {
    if (!showChatsType?.value) return null;
    return allData?.[showChatsType.value] || null;
  }, [allData, showChatsType]);

  const hasRevenueTrendChartData = useMemo(() => {
    const seriesList = currentRevenueTrendChartData?.seriesData;
    if (!Array.isArray(seriesList) || seriesList.length === 0) return false;
    return seriesList.some((series: any) => {
      if (!series || !Array.isArray(series.data)) return false;
      return series.data.some((point: any) => {
        if (point === null || point === undefined) return false;
        if (typeof point === 'object' && 'value' in point) {
          return point.value !== null && point.value !== undefined && point.value !== '';
        }
        if (point === '') return false;
        return true;
      });
    });
  }, [currentRevenueTrendChartData]);

  return (
    <>
      <div>
        {/* <div ref={topRef} style={{ position: 'relative', top: '-160px' }}></div> */}
        <div className='dbs-home-studio-card'>
          <MetricCard
            key={revenueCardConfig.title}
            coefficient={COEFFICIENT}
            config={revenueCardConfig}
            capsuleMode={'single'}
            capsuleHeight={40}
          />
          <MetricCard
            key={profitCardConfig.title}
            coefficient={COEFFICIENT}
            config={profitCardConfig}
            capsuleMode={'single'}
            capsuleHeight={40}
          />
        </div>
        <CardCommon
          title={rendTitle({
            title: `${formatMessage({ id: 'dbs_gross_revenue_trend' })} ($)`,
            tips: (
              <>
                <span>
                  {trendUpdateTime
                    ? `${formatMessage({ id: 'dbs_update_on' })} ${trendUpdateTime}`
                    : ''}
                </span>
                {(['bar', 'line'].includes(showChatsType?.value)) && (
                  <EventToggleSetting
                    showEventBubbles={chartEventSetting.showEventBubbles}
                    disabled={showEventsDisabled}
                    onToggle={(value) => {
                      const btnPoint = createBtnPoint(
                        OverviewTab,
                        'showEvents',
                        `Show Events - ${value ? 'On' : 'Off'}`,
                      );
                      if (btnPoint) {
                        SGBuriedBtnPoint(btnPoint);
                      }
                      setChartEventSetting({
                        ...chartEventSetting,
                        showEventBubbles: value,
                      });
                    }}
                    onOpenSettings={() => {
                      setEventSettingVisible(true);
                    }}
                  />
                )}
              </>
            ),
          })}
          operation={
            <RenderOperations
              defaultValue={showChatsType.value}
              defaultValueShow={showChatsType.format}
              change={handleShowChatsTypeChange}
              options={pickOperations(['view', ...showRevenueTrendList, 'download'], formatMessage)}
              downloadParams={{
                ...allData.table,
                name: formatMessage({ id: 'dbs_gross_revenue_trend' }),
              }}>
            </RenderOperations>
          }>
          {showChatsType?.value === 'bar' ? <Justify
            className="dbs-justify-half"
            bottom
            left={<div className='dbs-revenue-now-date'>
              {Object.keys(currentMetricsData).map((item: any) => <div className='dbs-home-overview-item' key={item}>
                <div className='dbs-home-overview-item-header'>
                  <div className='dbs-home-overview-item-title'>{currentMetricsData?.[item]?.title}</div>
                </div>
                <div className='dbs-revenue-date-desc'>{currentMetricsData?.[item]?.date}</div>
                <DataProcessing content={['$', currentMetricsData?.[item]?.content]} data={currentMetricsData?.[item]?.data} rate={currentMetricsData?.[item]?.rate || ''} />
              </div>)}

            </div>}
          // right={<RenderOperations
          //   defaultValue={showChatsType.value}
          //   defaultValueShow={showChatsType.format}
          //   change={handleShowChatsTypeChange}
          //   options={pickOperations(['view', ...showRevenueTrendList, 'download'], formatMessage)}>
          // </RenderOperations>}
          /> : <></>}
          {showChatsType?.value !== 'table'
            ? (trendLoading ? (
              <Loading style={{ height }} />
            ) : hasRevenueTrendChartData ? (
              <div style={{ position: 'relative' }}>
                <Chart
                  data={allData[showChatsType?.value]}
                  key={`${showChatsType?.value}${chartEventSetting.showEventBubbles}`}
                  type={showChatsType?.value}
                  height={height}
                  setOtherConfigFunc={(opt: any) => {
                    // Increase top spacing when event bubbles are visible.
                    if (chartEventSetting.showEventBubbles && Object.keys(chartEventSetting.bubbleEvent || {}).length > 0) {
                      opt.grid.top = 140;
                    }

                    opt = xAxisHighLight(opt, {
                    lineStyle: showChatsType?.value === 'bar' ? {
                      color: 'rgba(216, 218, 228, 0.3)',
                      width: 94,
                      type: 'solid',
                    } : {
                      color: '#BBBDCB',
                      width: 1,
                      type: 'solid',
                    }
                  });

                  // 自定义悬浮窗：仅对 Actual 和 Forecast 系列使用自定义 formatter
                  const actualName = formatMessage({ id: 'dbs_charts_legend_actual' });
                  const forecastName = formatMessage({ id: 'dbs_charts_legend_forecast' });

                  // 检查是否有 Actual 或 Forecast 系列
                  const hasActualOrForecast = opt.series?.some((series: any) => {
                    return series.name === actualName || series.name === forecastName;
                  });

                  if (hasActualOrForecast && opt.tooltip) {
                    const originalFormatter = opt.tooltip.formatter;
                    opt.tooltip.formatter = (params: any[]) => {
                      // 判断是否为默认添加的值
                      const flag = params.every((el: any) => el.value === undefined);
                      if (flag) return '';

                      // 检查是否包含 Actual 或 Forecast 系列
                      const hasTargetSeries = params.some((item: any) => {
                        return item.seriesName === actualName || item.seriesName === forecastName;
                      });

                      // 如果包含 Actual 或 Forecast，使用自定义 formatter（暂时和默认的一样）
                      if (hasTargetSeries) {
                        return `<div style="width: ${params[0]?.data?.tooltipWidth ? params[0]?.data?.tooltipWidth : params[0]?.data?.names ? '210px' : '170px'};font-size: 12px">
                          <div style="display:flex; justify-content: space-between;color: #D8DAE4">
                            <div style="width: 80px;">${params[0]?.name}</div>
                            ${params[0]?.data?.names?.split(',').map((item: string) => {
                          return `<div style="text-align: center">${item || ''}</div>`
                        }).join('') || ''}
                          </div>

                          ${params.map((item: any, index: number) => {
                          const itemData = item?.data || {};
                          if (itemData.render) {
                            return itemData.render(item);
                          }

                          // 如果是 Forecast 系列，检查是否与 Actual 值相等
                          let displayValue = itemData.tooltipValue ?? item.value;
                          if (item.seriesName === forecastName) {
                            // 找到同一数据点上的 Actual 值
                            const actualItem = params.find((p: any) => p.seriesName === actualName);
                            const actualValue = actualItem?.data?.tooltipValue ?? actualItem?.value;
                            const forecastValue = displayValue;

                            // 如果 forecast 和 actual 值相等，则显示 "-"
                            if (actualValue !== null && actualValue !== undefined &&
                              forecastValue !== null && forecastValue !== undefined &&
                              actualValue === forecastValue) {
                              displayValue = null; // 设置为 null 以显示 "-"
                            }
                          }

                          const hasDisplayValue = displayValue !== null && displayValue !== undefined;
                          const formattedValue = hasDisplayValue
                            ? (itemData.valueType
                              ? dealTypeValue(itemData.valueType, displayValue)
                              : mathFormat(displayValue))
                            : '-';
                          const safeFormattedValue = formattedValue ?? '-';

                          // 如果格式化后的值为 "-"，则不展示这一行
                          if (safeFormattedValue === '-' || safeFormattedValue === null || safeFormattedValue === undefined || safeFormattedValue === '0.0M') {
                            return '';
                          }

                          return `${itemData.totalShowContent && index === 0 ?
                            `<div class="dbs-base-chart-tooltip-item" style="display: flex;justify-content: space-between;margin-top:10px">
                                  <span class="dbs-base-chart-tooltip-item-name">
                                    ${itemData.totalTitleMarker || ''}
                                    <span style="font-size: 13px">${itemData.totalTitleText || 'Total'}</span>
                                  </span>
                                  <span class="dbs-base-chart-tooltip-item-value">
                                    <span style="font-size: 14px;font-weight: 600">${itemData.totalShowContent}</span>
                                  </span>
                                </div>` : ''}
                                ${itemData.totalShowContent && itemData.headSplit ? `<div style="height: 1px; width: 100%;background-color: rgba(255, 255, 255, 0.4);margin: 5px 0px 0px 0px"></div>` : ''}
                                <div class="dbs-base-chart-tooltip-item" style="display: flex;justify-content: space-between;margin-top:10px">
                                  <span class="dbs-base-chart-tooltip-item-name">
                                    ${item.marker}
                                    <span>${item.seriesName}</span>
                                  </span>
                                  <span class="dbs-base-chart-tooltip-item-value">
                                    <span style="font-size: 14px;font-weight: 600; color: ${itemData.textColor || ''}">${safeFormattedValue}</span>
                                    ${itemData.tip || ''}
                                  </span>
                                </div>
                              `;
                        }).join('')}
                        </div>`;
                      }

                      // 其他系列使用默认 formatter
                      if (originalFormatter && typeof originalFormatter === 'function') {
                        return originalFormatter(params);
                      }
                      return '';
                    };
                  }

                    return opt;
                  }}
                  onChartReady={(chartInstance: any) => {
                    chartRef.current = chartInstance;

                    // Wait for chart to fully render before setting ready state
                    // This ensures TopTipsEvent series is available AND convertToPixel returns valid values
                    const checkChartReady = () => {
                      const chartOption = chartInstance?.getOption();
                      const hasTopTipsEvent = chartOption?.series?.some((s: any) => s.name === 'TopTipsEvent');

                      if (hasTopTipsEvent) {
                        const xAxisData = chartOption?.xAxis?.[0]?.data || [];
                        const testXAxis = xAxisData.find((x: string) => x && x.length > 0);

                        if (testXAxis) {
                          try {
                            const testPosition = chartInstance.convertToPixel(
                              { seriesName: 'TopTipsEvent' },
                              [testXAxis, 0]
                            );
                            if (!isNaN(testPosition[0])) {
                              setIsChartReady(true);
                              return;
                            }
                          } catch (e) {
                            // convertToPixel not ready yet
                          }
                        }
                      }
                      setTimeout(checkChartReady, 100);
                    };

                    // Start checking
                    checkChartReady();
                  }}
                ></Chart>
                {(['bar', 'line'].includes(showChatsType?.value)) && isChartReady && chartEventSetting.showEventBubbles && chartRef.current && (
                  <EventWindows
                    key={Object.keys(chartEventSetting.bubbleEvent || {}).join(',')}
                    myChart={chartRef.current}
                    eventList={chartEventSetting.bubbleEvent}
                    format="YYYY-MM"
                    maxTimeValue={revenueData[revenueData.length - 1]?.month || ''}
                    showEventAxis={false}
                    dateType="monthly"
                    xAxisData={allData[showChatsType?.value]?.xAxisData || []}
                    yAxisMin={false}
                    topPadding={20}
                  />
                )}
              </div>
            ) : (
              <div style={{ minHeight: height || '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <NoData text={formatMessage({ id: 'dbs_empty_text' })} />
              </div>
            ))
            : (allData[showChatsType?.value]?.data && allData[showChatsType?.value]?.data.length > 0 ? (
              <TableContent
                className="dbs-commons-table dbs-chats-table"
                datas={allData[showChatsType?.value]?.data}
                columns={allData[showChatsType?.value]?.columns}
                sort={[{ by: 'fill_time', order: 'desc' }]}
                resultKey="list"
                recordKey="date"
                addons={[
                  scrollable({
                  }),
                ]}
              ></TableContent>
            ) : (
              <div style={{ minHeight: height || '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <NoData text={formatMessage({ id: 'dbs_empty_text' })} />
              </div>
            ))}
          <div>
            <div style={{ margin: '20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                {rendTitle({
                  title: 'Gross Revenue by Projects ($)',
                  tips: `${formatMessage({ id: 'dbs_update_on' })} ${projectsUpdateTime || ''}`,
                  // tips: !isGRBPEditing
                  //   ? `${formatMessage({ id: 'dbs_update_on' })} ${projectsUpdateTime || ''}`
                    // : `${formatMessage({ id: 'dbs_period' })}: ${buildPeriod(projectsUpdateTime)}`,
                  fontWeight: 600,
                })}
              </div>
              <div className='dbs-weekly-buttons-wrapper'>
                {buttonPermission.grossRevenueByProject && (!isGRBPEditing ? (
                  <Button
                    onClick={() => {
                      // Track buried point for Edit button
                      const btnPoint = createBtnPoint(
                        OverviewTab,
                        'grossRevenueByProjectsEdit',
                        'Gross Revenue by Projects - Edit'
                      );
                      if (btnPoint) {
                        SGBuriedBtnPoint(btnPoint);
                      }

                      // Use auto-save handler instead of direct handleEdit
                      handleGRBPEdit();
                      // Expand all projects when entering edit mode
                      // Use handleExpandedKeysChangeInternal with shouldTrack=false to prevent collapse/expand tracking
                      if (projectsData.length > 0) {
                        const allKeys = projectsData.map((item: any) => String(item.id));
                        handleExpandedKeysChangeInternal(allKeys, false);
                      }
                    }}
                    style={{ height: '30px', minWidth: '60px', fontSize: '12px' }}
                    className="dbs-gradient-btn"
                  >
                    {formatMessage({ id: 'dbs_edit' })}
                  </Button>
                ) : (
                  <div className="dbs-weekly-buttons-wrapper">
                    <Button
                      onClick={() => {
                        GRBPRef.current?.handleSave();
                      }}
                      style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                      className="dbs-gradient-btn"
                    >
                      {formatMessage({ id: 'dbs_confirm' })}
                    </Button>
                    <Button
                      onClick={() => {
                        GRBPRef.current?.handleCancel();
                        if (projectsData.length > 0) {
                          setExpandedKeys([String(projectsData[0]?.id)]);
                        }
                      }}
                      style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                      className="dbs-outline-btn"
                    >
                      {formatMessage({ id: 'dbs_cancel' })}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            {isGRBPEditing && (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
                <Button
                  type='weak'
                  onClick={handleAddProject}
                  className="dbs-add-project-btn"
                  style={{ height: '36px', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <img src={PlusIcon} alt="Add" className="dbs-add-project-icon" style={{ width: 16 }} />
                  {formatMessage({ id: 'dbs_add_project_category' })}
                </Button>
                <Select
                  options={GRBPOptions}
                  appearance="button"
                  placeholder="Move to"
                  value={moveToValue}
                  disabled={selectedCount === 0}
                  listHeight={Math.min(GRBPOptions.length * 36, 5.5 * 36)}
                  onChange={(value: string) => {
                    setMoveToValue(value);
                    if (value && GRBPRef.current) {
                      GRBPRef.current.batchMoveToCategory(value);
                      // 移动完成后清空下拉框（使用 setTimeout 确保在下一个事件循环清空）
                      setTimeout(() => {
                        setMoveToValue(undefined);
                      }, 0);
                    }
                  }}
                />
                <span style={{ color: '#888', fontSize: '14px' }}>
                  {selectedCount} {selectedCount === 1 ? 'item' : 'items'} selected
                </span>
              </div>
            )}
            {isGRBPEditing && (
              <div style={{ marginBottom: '18px', color: '#262832', fontSize: '14px' }}>
                {formatMessage({ id: 'dbs_25A_25F_25B_data_notice' }).replace(/25/g, getYearPrefix(projectsUpdateTime))}
              </div>
            )}
            <TableContent
              ref={GRBPRef}
              className="dbs-revenue-projects-table"
              recordKey={(record: any) =>
                // 这里要注意，原始数据中数据类型全是字符串，不转成字符串会导致 key 值匹配不上
                String(record.id)
              }
              datas={projectsData}
              loading={projectsLoading}
              columns={projectColums(maxVsVallue, formatMessage, projectsUpdateTime, { isAllExpanded, onToggleExpandAll: toggleExpandAll })}
              editColumns={editProjectColums(maxVsVallue, formatMessage, projectsUpdateTime)}
              onCancelRefetch={fetchGrossRevenueByProject}
              originalData={originalProjectsData}
              addons={[
                // 支持表格滚动，高度超过 192 开始显示滚动条
                // scrollable({
                //   maxHeight: '310px',
                //   // minWidth: 2000,
                // }),
                expandable({
                  // 已经展开的产品
                  expandedKeys,
                  // 产品展开为消息行
                  expand: record => record.children || null,
                  // 发生展开行为时，回调更新展开键值 (with buried point tracking)
                  onExpandedKeysChange: handleExpandedKeysChange,
                  // 只有产品行允许展开
                  shouldRecordExpandable: record => !isGRBPEditing && Boolean(record.children),
                  targetColumnKey: "Project",
                  rowExpand: true
                }),
                // indentable({
                //   // 缩进放在「消息类型」列上
                //   indent: 16,
                //   targetColumnKey: "Project",
                //   // 提供层级关系
                //   relations,
                // }),
                injectable({
                  row: (props, context) => ({
                    style: {
                      ...(props.style || {}),
                      background: context.record?.children ? "#F5F5F8" : undefined,
                    },
                  }),
                }),
                mergeable({
                  headColSpan: columnIndex => {
                    // 编辑模式下由于有多选框列，列索引向右偏移 1
                    const offset = isGRBPEditing ? 2 : 0;
                    const col4Index = 4 + offset; // "25F VS 25B" (负值列)
                    const col5Index = 5 + offset; // "25F VS 25B2" (正值列)

                    switch (columnIndex) {
                      case col4Index:
                        return 2; // 合并两列
                      case col5Index:
                        return 0; // 被合并
                    }
                    return 1;
                  },
                }),
              ]}
              // 拖拽addon需要单独处理，通过editingAddons传递
              editingAddons={[
                draggable({
                  expandedKeys,
                  relations,
                  dragType: "tree",
                  childrenColName: "children",
                  onExpandedKeysChange: (keys: any) => {
                    setExpandedKeys(keys);
                  },
                  onDragEnd: (records: any, dragContext: any) => {
                    // 验证拖拽结果，确保项目不会被嵌套到其他项目中，以及目录不会被嵌套到其他目录中
                    const validateDragResult = (data: any[]): { isValid: boolean; errorType: 'category' | 'project' | 'standalone' | null } => {
                      // 首先检查顶层：所有顶层项目必须是目录（category）
                      for (const topLevelItem of data) {
                        if (topLevelItem.isCategory === false) {
                          console.warn('Invalid drag: Project cannot be at top level (standalone)', topLevelItem);
                          return { isValid: false, errorType: 'standalone' };
                        }
                      }

                      const checkNesting = (items: any[], depth: number = 0, parentIsCategory: boolean = false): { isValid: boolean; errorType: 'category' | 'project' | 'standalone' | null } => {
                        for (const item of items) {
                          // 如果是项目（非目录），它不应该有 children
                          if (item.isCategory === false && item.children && Array.isArray(item.children) && item.children.length > 0) {
                            console.warn('Invalid drag: Project cannot have children', item);
                            return { isValid: false, errorType: 'project' };
                          }

                          // 如果是目录，它不应该被嵌套到另一个目录中（目录只能在顶层）
                          if (item.isCategory === true && parentIsCategory) {
                            console.warn('Invalid drag: Category cannot be nested inside another category', item);
                            return { isValid: false, errorType: 'category' };
                          }

                          // 递归检查子项
                          if (item.children && Array.isArray(item.children)) {
                            const result = checkNesting(item.children, depth + 1, item.isCategory === true);
                            if (!result.isValid) {
                              return result;
                            }
                          }
                        }
                        return { isValid: true, errorType: null };
                      };
                      return checkNesting(data);
                    };

                    // 验证拖拽结果
                    const validationResult = validateDragResult(records);
                    if (!validationResult.isValid) {
                      console.warn('Drag validation failed: Invalid nesting detected.', validationResult);

                      let errorMessage = '';
                      switch (validationResult.errorType) {
                        case 'category':
                          errorMessage = 'Invalid drag operation. Categories cannot be nested inside other categories.';
                          break;
                        case 'project':
                          errorMessage = 'Invalid drag operation. Projects cannot be nested inside other projects.';
                          break;
                        case 'standalone':
                          errorMessage = 'Invalid drag operation. Projects must be inside a category and cannot be standalone.';
                          break;
                        default:
                          errorMessage = 'Invalid drag operation. Please try again.';
                      }

                      notification.error({
                        description: errorMessage,
                      });

                      // 使用 setTimeout 确保在拖拽操作完成后才展开（避免与 draggable addon 的 onExpandedKeysChange 冲突）
                      setTimeout(() => {
                        // 使用 projectsData 而不是 getTableData，因为这是我们的真实数据源
                        const currentData = projectsData.length > 0 ? projectsData : (GRBPRef.current?.getTableData?.() || []);
                        console.log('🔄 [DEBUG] Expanding categories after validation failure, data length:', currentData.length);
                        expandAllCategoriesWithChildren(currentData);
                      }, 0);

                      return; // 阻止无效的拖拽
                    }

                    // 获取当前的 editData（包含用户的编辑）
                    const currentEditData = GRBPRef.current?.getEditData?.() || [];

                    // 创建一个 Map 用于快速查找编辑数据（递归构建）
                    const editDataMap = new Map();
                    const buildEditMap = (data: any[]) => {
                      data.forEach((item: any) => {
                        editDataMap.set(String(item.id), item);
                        if (item.children && Array.isArray(item.children)) {
                          buildEditMap(item.children);
                        }
                      });
                    };
                    buildEditMap(currentEditData);

                    // 创建一个 Set 来跟踪已处理的项目ID，防止重复
                    const processedProjectIds = new Set<string>();

                    // 递归合并数据，保留所有用户编辑的字段
                    // 同时清理任何无效的嵌套结构（项目不应该有 children）
                    // 并确保每个项目只出现一次（防止拖拽后重复）
                    const mergeEditData = (records: any[]): any[] => {
                      return records
                        .map((record: any) => {
                          const editedRecord = editDataMap.get(String(record.id));
                          const merged = {
                            ...record,
                            // 保留用户编辑的所有字段（如果有编辑记录）
                            ...(editedRecord ? {
                              Project: editedRecord.Project,
                              'Opportunities & Risks': editedRecord['Opportunities & Risks'],
                              'Action & Note': editedRecord['Action & Note'],
                              isCategory: editedRecord.isCategory,
                            } : {}),
                          };

                          // 如果这是项目（非目录），确保没有 children
                          if (merged.isCategory === false) {
                            // 移除任何 children，因为项目不能有子项
                            if (merged.children) {
                              delete merged.children;
                            }

                            // 检查是否已经处理过这个项目（防止重复）
                            const projectId = String(merged.id);
                            if (processedProjectIds.has(projectId)) {
                              // 这个项目已经在其他地方出现了，跳过这个实例（避免重复）
                              return null;
                            }
                            processedProjectIds.add(projectId);
                          } else {
                            // 对于目录，保留 children 字段（递归处理子项）
                            // 只使用 drag result (record) 的结构，不信任 editData 的结构（可能包含旧位置）
                            if (record.children && Array.isArray(record.children)) {
                              merged.children = mergeEditData(record.children);
                              // 过滤掉 null 值（被标记为重复的项目）
                              merged.children = merged.children.filter((child: any) => child !== null);
                            } else {
                              // 如果 drag result 中没有 children，设置为空数组（不信任 editData 的结构）
                              merged.children = [];
                            }
                          }

                          return merged;
                        })
                        .filter((record: any) => record !== null); // 移除被标记为重复的项目
                    };

                    const mergedRecords = mergeEditData(records);

                    // 清理空目录的 children 数组，确保一致性
                    const cleanupEmptyCategories = (items: any[]): any[] => {
                      return items.map((item: any) => {
                        if (item.isCategory === true) {
                          // 确保目录有 children 数组（即使为空）
                          if (!item.children || !Array.isArray(item.children)) {
                            item.children = [];
                          }
                          // 递归清理子目录
                          item.children = cleanupEmptyCategories(item.children);
                        }
                        return item;
                      });
                    };
                    const cleanedRecords = cleanupEmptyCategories(mergedRecords);

                    // 再次验证合并后的数据
                    const secondValidationResult = validateDragResult(cleanedRecords);
                    if (!secondValidationResult.isValid) {
                      console.warn('Drag validation failed after merge. Reverting drag.', secondValidationResult);

                      let errorMessage = '';
                      switch (secondValidationResult.errorType) {
                        case 'category':
                          errorMessage = 'Invalid drag operation. Categories cannot be nested inside other categories.';
                          break;
                        case 'project':
                          errorMessage = 'Invalid drag operation. Projects cannot be nested inside other projects.';
                          break;
                        default:
                          errorMessage = 'Invalid drag operation. Please try again.';
                      }

                      notification.error({
                        description: errorMessage,
                      });

                      // 使用工具函数简化展开逻辑
                      expandCategoriesAfterDrag(projectsData, GRBPRef, expandAllCategoriesWithChildren, 0);

                      return;
                    }

                    // 重新计算一级目录的汇总数据（因为拖拽可能改变了子项目的位置）
                    const recalculatedData = recalculateCategoryTotals(cleanedRecords);

                    // 更新数据
                    setProjectsData(recalculatedData);
                    if (GRBPRef.current) {
                      GRBPRef.current.setEditData(recalculatedData);
                      GRBPRef.current.setTableData(recalculatedData);
                    }

                    // 使用setTimeout确保更新完成后再展开所有目录
                    setTimeout(() => {
                      // 展开所有有 children 的目录（操作完成后统一处理）
                      expandAllCategoriesWithChildren(recalculatedData);
                    }, 100);
                  },
                })
              ]}
              editable={true}
              userPermissions={{ canEdit: buttonPermission.grossRevenueByProject }}
              hideButtons={true}
              onEditingChange={handleGRBPEditingChange}
              validateFields={['Project']}
              validateFilter={(record: any) =>
                // 只检查一级分类（有 children 的行）
                record.children && Array.isArray(record.children)
              }
              onSave={handleSaveProjects}
              enableSelection={true}
              selectionRelations={relations}
              onSelectionChange={(keys: string[]) => {
                setSelectedCount(keys.length);
              }}
              onEditDataChange={(data: any) => {
                setGRBPOEditData(data);
              }}
              onBatchMoveToCategory={(data: any, targetCategoryId: string) => {
                // 重新计算每个一级目录的汇总数据（25A、25F、25B）
                const recalculatedData = data.map((category: any) => {
                  // 如果有 children 属性（一级目录），重新计算汇总
                  if (category.children !== undefined) {
                    if (Array.isArray(category.children) && category.children.length > 0) {
                      // 有子项目：计算所有二级项目的汇总
                      const totals = category.children.reduce(
                        (acc: any, child: any) => ({
                          '25A': (acc['25A'] || 0) + (child['25A'] || 0),
                          '25F': (acc['25F'] || 0) + (child['25F'] || 0),
                          '25B': (acc['25B'] || 0) + (child['25B'] || 0),
                        }),
                        { '25A': 0, '25F': 0, '25B': 0 }
                      );

                      return {
                        ...category,
                        '25A': totals['25A'],
                        '25F': totals['25F'],
                        '25B': totals['25B'],
                      };
                    }
                    // 没有子项目：设置为 0
                    return {
                      ...category,
                      '25A': 0,
                      '25F': 0,
                      '25B': 0,
                    };

                  }
                  // 二级项目保持不变
                  return category;
                });

                setExpandedKeys((prevKeys: string[]) => {
                  if (!prevKeys.includes(targetCategoryId)) {
                    return [...prevKeys, targetCategoryId];
                  }
                  return prevKeys;
                });
                return recalculatedData;
              }}
              emptyContent={
                GRBPRef.current?.canEdit && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '320px' }}>
                    No Data. Please
                    <span onClick={() => {
                      GRBPRef.current?.handleEdit();
                      setTimeout(() => {
                        handleAddProject();
                      }, 0);
                    }} style={{ cursor: 'pointer', color: '#7F6FF1', marginLeft: '4px' }}>Add Project Category</span>
                  </div>
                )
              }
            ></TableContent>
          </div>
        </CardCommon>

        <DecisionPointsList
          ref={decisionRef}
          selectedMonth={selectedMonth}
          maxDataTime={maxDataTime}
          canEdit={buttonPermission.decisionPointsList}
          isGRBPEditing={isGRBPEditing}
          isNotesEditing={isNotesEditing}
          onDPTEditingChange={setIsDPTEditing}
          onSectionEditingChange={onEditingChange}
        />
        <PlainText
          url="/api/v1/mgmt_pc/note/get"
          editUrl="/api/v1/mgmt_pc/note/update"
          params={{
            source: "overview",
            logical_flag: "has_date_latest"
          }}
          showDatePicker={false}
          showTimeInTitle={true}
          selectedMonth={selectedMonthMoment}
          maxDataTime={maxDataTime}
          editable={true}
          userPermissions={{ canEdit: buttonPermission.notes, noAccessHide: getAppendixNotesPermission('publishing_overview_notes') }}
          tipString={'Data is updated by the Business Operations Management team.'}
          onEditingChange={handleNotesEditingChange}
          onEditClick={(type) => {
            // Track buried point for Notes Edit button or Add Content link click
            const extInfo = type === 'Add Content Link'
              ? 'Notes Edit - Add Content Link'
              : 'Notes Edit - Edit Button';
            const btnPoint = createBtnPoint(
              OverviewTab,
              'notesEdit',
              extInfo
            );
            if (btnPoint) {
              SGBuriedBtnPoint(btnPoint);
            }
          }}
          // Auto-save feature
          autoSavePageKey={EDIT_PAGE_KEY_MAP.publishing_overview_notes}
        />

        {/* Event Setting Modal */}
        <EventSetting
          visible={eventSettingVisible}
          close={() => setEventSettingVisible(false)}
          confirm={(params: any) => {
            setChartEventSetting((prev: any) => ({
              ...prev,
              bubbleEvent: params.bubbleEvent,
            }));
            setEventSettingVisible(false);
          }}
          data={eventDataByGame}
          timeFormat="YYYY-MM"
          dateType="monthly"
          xAxisData={allData[showChatsType?.value]?.xAxisData?.filter((x: string) => x !== '') || []}
        />

        {/* 🚀 Auto-save recovery modal for Gross Revenue by Projects */}
        <AutoSaveModal
          visible={GRBPAutoSaveVisible}
          onConfirm={handleGRBPAutoSaveConfirm}
          onClose={handleGRBPAutoSaveCancel}
        />

        {/* 🚀 RoutePrompt for Gross Revenue by Projects */}
        <RoutePrompt
          when={hasGRBPUnsavedChanges}
          onOk={async () => {
            const editedData = GRBPRef.current?.getEditData?.() || [];
            await handleSaveProjects(editedData);
            clearGRBPAutoSave();
            setIsGRBPEditing(false);
          }}
        />
      </div>
    </>
  );
});

export default PublishingManagement;
